import { Icon, Modal as ZModal } from "@zapier/design-system";
import {
  ChangeEvent,
  ComponentProps,
  forwardRef,
  useMemo,
  ReactNode,
} from "react";
import { createPortal } from "react-dom";
import ReactTextareaAutosize from "react-textarea-autosize";
import { cva, type VariantProps } from "class-variance-authority";
import isNumber from "lodash/isNumber";

import { cn } from "./utils";
import { STRINGS } from "./constants";

/**
 * Simple wrapper for Zinnia's Modal that renders it in a React Portal.
 */
function InnerModal(props: ComponentProps<typeof ZModal>) {
  const renderModal = () => <ZModal {...props} />;

  if (document) {
    return createPortal(renderModal(), document.body);
  }

  return renderModal();
}

export function Modal(props: { onClosed: () => void; children: ReactNode }) {
  return (
    <InnerModal onClosed={props.onClosed}>
      <div className="relative min-h-screen w-full min-w-[100vw] overflow-x-hidden overflow-y-scroll rounded-none bg-zi-white p-5 text-zi-text shadow-none sm:max-h-[650px] sm:min-h-min sm:min-w-[550px] sm:max-w-4xl sm:rounded-xl sm:shadow-lg">
        {props.children}
        <button
          type="button"
          className="absolute right-4 top-4 cursor-pointer text-zi-gray"
          onClick={() => props.onClosed()}
        >
          <Icon name="formX" size={30} />
          <span className="sr-only">{STRINGS["Close"]}</span>
        </button>
      </div>
    </InnerModal>
  );
}

type Props = Omit<
  React.ComponentPropsWithoutRef<typeof ReactTextareaAutosize>,
  "value" | "onChange"
> & {
  value?: string;
  onChange?: (value: string) => void;
  isErrored?: boolean;
  disableResize?: boolean;
};

export const TextArea = forwardRef<HTMLTextAreaElement, Props>(
  function TextArea({ onChange, isErrored, disableResize, ...props }, ref) {
    const handleChange = useMemo(() => {
      return onChange !== undefined
        ? (e: ChangeEvent<HTMLTextAreaElement>) => {
            onChange(e.target.value);
          }
        : undefined;
    }, [onChange]);

    return (
      <ReactTextareaAutosize
        {...props}
        ref={ref}
        onChange={handleChange}
        className={cn(
          "block min-h-[50px] w-full rounded-lg border-2 border-solid border-zi-lightGray bg-zi-superLightGray p-[15px] text-[17px] text-zi-text focus-within:border-zi-primary",
          {
            "resize-none": disableResize,
            "resize-y": !disableResize,
            "border-zi-formError": isErrored,
          }
        )}
        value={props.value ?? ""}
      />
    );
  }
);

const spinnerSizes = {
  small: "h-[20px] w-[20px] border-4",
  medium: "h-[30px] w-[30px] border-6",
  large: "h-[60px] w-[60px] border-8",
} as const;

const spinnerColors = {
  light: "text-zi-pageBackground",
  dark: "text-zi-text",
};

type SpinnerProps = {
  ariaLabel?: string;
  className?: string;
  height?: number | string;
  size?: keyof typeof spinnerSizes;
  color?: keyof typeof spinnerColors;
};

export function Spinner({
  ariaLabel = STRINGS["Loading"],
  className = "",
  height = "auto",
  size = "small",
  color = "dark",
}: SpinnerProps) {
  const parsedHeight = isNumber(height) ? `${height}px` : height;

  return (
    <div
      className="mx-auto my-10 flex items-center justify-center"
      style={{ height: parsedHeight }}
    >
      <div
        data-testid="spinner"
        role="status"
        className={cn(
          "aspect-square animate-spin rounded-[50%] border-current border-r-transparent opacity-70",
          spinnerSizes[size],
          spinnerColors[color],
          className
        )}
      >
        <span className="sr-only">{ariaLabel}</span>
      </div>
    </div>
  );
}

export type ButtonColor = "primary" | "secondary" | "danger";

const buttonVariants = cva(
  "relative w-full cursor-pointer rounded-lg border border-2 border-solid px-[15px] py-0 transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-zi-white active:brightness-100 disabled:cursor-not-allowed disabled:opacity-80 [&:not(:disabled):hover]:brightness-110",
  {
    variants: {
      color: {
        primary:
          "border-zi-primary bg-zi-primary font-semibold text-zi-white focus-visible:ring-zi-primary",
        secondary:
          "border-zi-primary bg-zi-white font-medium text-zi-primary focus-visible:ring-zi-primary",
        danger:
          "border-zi-danger bg-zi-danger font-semibold text-zi-white focus-visible:ring-zi-danger",
      },
      size: {
        small: "h-10 text-[15px]",
        medium: "h-[50px] text-[17px]",
      },
    },
    defaultVariants: {
      color: "primary",
      size: "medium",
    },
  }
);

interface ButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    VariantProps<typeof buttonVariants> {
  isLoading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    { className, color, size, isLoading, children, ...props },
    ref
  ) {
    return (
      <button
        className={cn(buttonVariants({ className, color, size }))}
        disabled={isLoading ?? props.disabled}
        ref={ref}
        {...props}
      >
        <span style={{ opacity: isLoading ? 0 : 1 }}>{children}</span>
        {isLoading && (
          <span className="absolute inset-0 flex items-center justify-center">
            <Spinner className="text-current" />
          </span>
        )}
      </button>
    );
  }
);

export const Badge: React.FunctionComponent<
  React.ComponentPropsWithoutRef<"span">
> = (props) => (
  <span
    {...props}
    className={cn(
      "inline-flex h-6 items-center justify-center rounded-full bg-zi-primary-700 px-3 py-2 font-mono text-sm text-zi-primary-50",
      props.className
    )}
  />
);
